import React, {Component} from 'react';
import h from 'react-hyperscript';
import './App.scss';
import Header from 'Components/Header/Header';
import Main from 'Components/Main/Main';
import Footer from 'Components/Footer/Footer';
import LoadingIndicator from 'Components/LoadingIndicator/LoadingIndicator';
import Dialog from '@material-ui/core/Dialog';
import Settings from 'Pages/Settings/Settings';
import GdprAnnouncement from 'Components/GdprAnnouncement/GdprAnnouncement';
import {withRouter} from 'react-router';
import {createMuiTheme, MuiThemeProvider} from '@material-ui/core/styles';
import createPalette from '@material-ui/core/styles/createPalette';
import content from 'Assets/data/globals.json';
import ErrorPage from 'Components/ErrorPage/ErrorPage';
import {
  ENDPOINTS,
  CLIENT_ID,
  MAINTENANCE_MODE,
  optimizedResize,
  isPrivateMode,
  fetchTimeout} from 'Src/utils/utils';
import {AUTHENTICATION} from 'Src/utils/gtm/const';
import {pushEvent as pushGTMEvent} from 'Src/utils/gtm';
import ErrorMessage from 'Components/ErrorMessage/ErrorMessage';
import CountryError from 'Components/ErrorMessage/CountryError';
import {
  isSmallViewport,
  getScreen,
} from 'Src/utils/utils';

optimizedResize();

const SCOPES = {
  BASIC: 'profile email https://www.googleapis.com/auth/admin.directory.user.readonly',
  ADDITIONAL: 'profile email https://www.googleapis.com/auth/admin.directory.user.readonly',
};

export const OptedInContext = React.createContext({
  optedIn: false,
  setOptedIn: () => {},
  setLoggedOut: () => {},
});

const theme = createMuiTheme({
  typography: {
    useNextVariants: true,
    htmlFontSize: 10,
  },
  palette: createPalette({
    common: {
      black: '#000',
      white: '#fff',
    },
    primary: {
      50: '#e8f0fe',
      100: '#d2e3fc',
      200: '#aecbfa',
      300: '#8ab4f8',
      400: '#669df6',
      500: '#4285f4',
      600: '#1a73e8',
      700: '#1967d2',
      800: '#185abc',
      900: '#174ea6',
      main: '#4285f4',
    },
    secondary: {
      50: '#f8f9fa',
      100: '#f1f3f4',
      200: '#e8eaed',
      300: '#dadce0',
      400: '#bdc1c6',
      500: '#9aa0a6',
      600: '#80868b',
      700: '#5f6368',
      800: '#3c4043',
      900: '#202124',
      main: '#9aa0a6',
    },
  }),
  overrides: {
    Menu: {
      text: {
        color: '#fff',
      },
    },
    MuiTooltip: {
      tooltip: {
        fontSize: '0.8em',
      },
    },
  },
});

class App extends Component {
  constructor(props) {
    super(props);
    this._isMounted = false;
    this.state = {
      isLoaded: false,
      googleUser: null,
      userStatus: null,
      adminLevel: null,
      isAdmin: false,
      isLoggedIn: false,
      needsDistrictConfig: false,
      errorMessage: null,
      errorPrefix: null,
      csrf: null,
      isSettingsOpen: false,
      showAccountPicker: false,
      surveySlug: null,
      scopes: null,
      allowModalClose: false,
      settingsModalId: 'primary',
      mobileLogin: null,
      errorPage: null,
      productWindowsLoading: false,
      settingsSaveRequired: false,
      scopesGranted: false,
    };
    this.gapi = window.gapi;
    this.csrfInterval = 2700000; // 45 minutes
    this.logout = this.logout.bind(this);
    this.login = this.login.bind(this);
    this.onLoad = this.onLoad.bind(this);
    this.initGapi = this.initGapi.bind(this);
    this.checkIfLoggedInGapi = this.checkIfLoggedInGapi.bind(this);
    this.handleSettingsOpen = this.handleSettingsOpen.bind(this);
    this.handleSettingsClose = this.handleSettingsClose.bind(this);
    this.setAllowModalClose = this.setAllowModalClose.bind(this);
    this.setSettingsModalId = this.setSettingsModalId.bind(this);
    this.fetchUserStatus = this.fetchUserStatus.bind(this);
    this.setOptedIn = this.setOptedIn.bind(this);
    this.setCurrentReportName = this.setCurrentReportName.bind(this);
    this.setLatestReportName = this.setLatestReportName.bind(this);
    this.setProductWindowsLoading = this.setProductWindowsLoading.bind(this);
    this.setScopesGranted = this.setScopesGranted.bind(this);
  }

  componentWillMount() {
    this.onResize();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  onLoad() {
    if (!navigator.cookieEnabled) {
      this.setState({
        isLoaded: true,
        errorPage: 'cookie',
      });
      return;
    }
    const maintenanceMode = this.checkMaintenanceMode();
    if (maintenanceMode) return;
    this.state.mobileLogin = new URLSearchParams(this.props.location.search)
        .get('mobileLogin');

    this.decideProperScopes();

    const self = this;

    isPrivateMode().then((isPrivate) => {
      if (isPrivate) {
        this.setState({
          errorPage: 'cookie',
        });
        self.setIsLoaded(true);
      } else {
        if (this.gapi) {
          self.gapi.load('auth2', self.initGapi);
          self.getCsrf();
          self.startCsrfCounter();
        } else {
          if (document.referrer !== document.location.href) {
            setTimeout(() => {
              document.location.reload();
            }, 5000);
          }
        }
      }
    });
  }

  checkMaintenanceMode() {
    const inMaintenanceMode = MAINTENANCE_MODE && MAINTENANCE_MODE !== 'false';
    const maintenanceBackdoor = window.localStorage.getItem('__sherlock_admin');
    const maintenanceOveride = maintenanceBackdoor &&
      maintenanceBackdoor == MAINTENANCE_MODE;
    const showMaintenanceMode = inMaintenanceMode && !maintenanceOveride;

    if (showMaintenanceMode) {
      this.setErrorMessage(
          h(ErrorMessage, {
            prefix: content.errors.maintenance_mode.prefix,
            message: content.errors.maintenance_mode.message,
          })
      );
      this.setState({maintenanceMode: true});
      this.setIsLoaded(true);
    }

    return showMaintenanceMode;
  }

  initGapi() {
    if (this.state.scopes) {
      this.gapi.auth2.init({
        clientId: CLIENT_ID,
        scope: this.state.scopes,
        plugin_name: 'Transformation Report',
      }).then(this.checkIfLoggedInGapi)
          .catch((error) => {
            if (error.name === 'AbortError') {
              this.setState({
                errorPage: 'timeout',
              });
            } else if (error.details ===
                'Cookies are not enabled in current environment.') {
              this.setState({
                errorPage: 'cookie',
              });
            }
            this.setState({
              isLoaded: true,
            });
          });
    }
  }

  decideProperScopes() {
    let scopes = SCOPES.ADDITIONAL;
    if (window.location.href.includes('survey')) {
      scopes = SCOPES.BASIC;
    }
    this.setState({
      scopes: scopes,
    });
  }

  checkIfLoggedInGapi() {
    this.setIsLoaded(false);
    const authInstance = this.gapi.auth2.getAuthInstance();
    const isLoggedInWithGapi = authInstance.isSignedIn.get();
    if (isLoggedInWithGapi) {
      this.setGoogleUser(authInstance.currentUser.get());
      this.fetchUserStatus();
    } else {
      this.setIsLoaded(true);
    }
  }

  getCsrf() {
    fetch(ENDPOINTS.CSRF, {
      method: 'GET',
    }).then((response) => response.json()).then((result) => {
      if (result.csrf_token) {
        this.setCsrf(result.csrf_token);
      }
    });
  }

  setCsrf(value) {
    this.setState({
      csrf: value,
    });
  }

  startCsrfCounter() {
    setInterval(this.getCsrf.bind(this), this.csrfInterval);
  }

  csrfTimer() {
    this.getCsrf();
  }

  checkSettingsPrompt() {
    if (!this.state.userStatus.product_windows_set_flag &&
        this.state.isAdmin &&
        Boolean(this.state.isOptedIn)) {
      this.handleSettingsOpen(true);
    }
  }

  fetchUserStatus(credentials) {
    const idToken = this.state.googleUser.getAuthResponse().id_token;
    const accessToken = this.state.googleUser.getAuthResponse().access_token;
    const hasGrantedScopes =
      this.state.googleUser.hasGrantedScopes(this.state.scopes);
    return fetchTimeout(ENDPOINTS.USER_STATUS, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': idToken,
        'X-CSRF-Token': this.state.csrf,
      },
      body: JSON.stringify({
        access_token: accessToken,
        granted_scopes: hasGrantedScopes,
      }),
    })
        .then((response) => {
          return response.json();
        })
        .then((result) => {
          const adminLevel = result.admin_level;
          if (adminLevel) {
            this.setUserStatus(result);
            this.setErrorMessage(null);
            window.localStorage.setItem('districtId', result.district_id);
            window.localStorage.setItem('primaryDomain', result.primary_domain);
            window.localStorage.setItem('surveyId', result.survey_id);

            this.checkSettingsPrompt();

            pushGTMEvent(AUTHENTICATION.TAG, {
              event: AUTHENTICATION.EVENT,
              action: {authentication_action: AUTHENTICATION.ACTION.LOGIN},
              variables: {
                adminLevel,
                district_domain: result.domain,
                district_name: result.district_name,
              },
            });
          } else if (result.message) {
            this.setUserStatus(null);

            let error;
            if (result.message === 'User country not allowed') {
              error = h(CountryError);
            } else if (result.message === 'User is not part of valid EDU') {
              this.setState({
                errorPage: 'nonEdu',
              });
            } else {
              error = h(ErrorMessage, {message: result.message});
            }

            this.setErrorMessage(error);
            this.logout();
          }
        })
        .then((result) => {
          return fetchTimeout(ENDPOINTS.AUTHENTICATE, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': idToken,
              'X-CSRF-Token': this.state.csrf,
            },
            body: JSON.stringify({
              access_token: accessToken,
            }),
          }).catch((error) => {
            if (error.name === 'AbortError') {
              this.setState({
                errorPage: 'timeout',
              });
            }
          });
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            this.setState({
              isLoggedIn: false,
              errorPage: 'timeout',
            });
          }
          this.setIsLoaded(true);
          this.setUserStatus(null);
          this.setErrorMessage(h(ErrorMessage,
              {message: content.errors.fetch}
          ));
        });
  }

  checkScopes() {
    const scopes = this.state.scopes;
    const hasGrantedScopes = this.state.googleUser.hasGrantedScopes(scopes);
    this.setScopesGranted(hasGrantedScopes);
  }

  promptScopes() {
    const options = {
      scope: this.state.scopes,
    };
    this.state.googleUser.grant(options).then((resp) => {
      this.setIsLoaded(false);
      this.fetchUserStatus(resp.code);
      this.checkScopes();
    });
  }

  logout() {
    this.gapi.auth2.getAuthInstance().signOut().then(() => {
      this.setState({
        isLoaded: true,
        isLoggedIn: false,
        isAdmin: false,
        googleUser: null,
        userStatus: null,
        showAccountPicker: true,
        isOptedIn: false,
      });
      this.props.history.push('/app/');
    });
  }

  login(e) {
    e.preventDefault();
    let options;
    if (this.state.showAccountPicker) {
      options = new this.gapi.auth2.SigninOptionsBuilder();
      options.setPrompt('select_account');
    }
    this.gapi.auth2.getAuthInstance().signIn(options).then(() => {
      this.checkIfLoggedInGapi();
    });
  }

  onSetUserStatus() {
    this.setIsLoaded(true);
    if (this.state.userStatus) {
      if (this.state.userStatus.admin_level === 'Admin') {
        this.setIsAdmin(true);
        this.checkModalType();
      } else {
        this.setIsAdmin(false);
      }
    }
  }

  checkModalType() {
    const isOptedIn = Boolean(this.state.isOptedIn);

    if (this.state.userStatus.show_alternate_settings === true) {
      this.setShowSettings(isOptedIn);
      this.setSettingsModalId('secondary');
    } else if (this.state.userStatus.show_alternate_settings === false) {
      this.setShowSettings(isOptedIn);
      this.setSettingsModalId('primary');
    }
  }

  setSettingsModalId(value) {
    this.setState({
      settingsModalId: value,
    });
  }

  setUserStatus(value) {
    this.setState({
      userStatus: value,
    }, () => {
      if (this.state.userStatus) {
        this.setOptedIn(Boolean(this.state.userStatus.email_opt_in_date));
      }
      this.onSetUserStatus();
    });
  }

  setIsLoaded(value) {
    this.setState({
      isLoaded: value,
    });
  }

  setIsAdmin(value) {
    this.setState({
      isAdmin: value,
    });
  }

  setGoogleUser(value) {
    this.setState({
      googleUser: value,
    }, () => {
      if (this.state.googleUser) {
        this.setIsLoggedIn(true);
        this.checkScopes();
      } else {
        this.setIsLoggedIn(false);
      }
    });
  }

  setIsLoggedIn(value) {
    this.setState({
      isLoggedIn: value,
    });
  }

  setCurrentReportName(value) {
    this.setState({
      currentReportName: value,
    });
  }

  setLatestReportName(value) {
    this.setState({
      latestReportName: value,
    });
  }

  setAdminLevel(value) {
    this.setState({
      adminLevel: value,
    }, () => {
      if (value && value.admin_level === 'Admin') {
        this.setIsAdmin(true);
      } else {
        this.setIsAdmin(false);
      }
    });
  }

  handleSettingsClose() {
    this.setState({isSettingsOpen: false}, () => {
      if (this.state.settingsModalId === 'secondary') {
        this.setSettingsModalId('primary');
      }
    });
  }

  handleSettingsOpen(saveRequired=false) {
    this.setState({
      isSettingsOpen: true,
      settingsSaveRequired: saveRequired,
    });
  }

  setAllowModalClose(value) {
    this.setState({
      allowModalClose: value,
    });
  }

  setShowSettings(value) {
    this.setState({
      isSettingsOpen: value,
      needsDistrictConfig: value,
    });
  }

  setOptedIn(value, userOptedOut) {
    this.setState({
      isOptedIn: value,
    }, () => {
      if (userOptedOut) {
        this.logout();
      }
      if (this.state.isOptedIn &&
          this.state.userStatus.admin_level === 'Admin') {
        this.checkSettingsPrompt();
        this.checkModalType();
      }
    });
  }

  setErrorMessage(errorMessage) {
    this.setState({errorMessage});
  }

  setProductWindowsLoading(value) {
    this.setState({
      productWindowsLoading: value,
    });
  }

  setScopesGranted(value) {
    this.setState({
      scopesGranted: value,
    });
  }

  onResize() {
    if (!this._isMounted && this.resizeListener) {
      return window.removeEventListener('optimizedResize', this.resizeListener);
    }

    const setDynamicSizes = () => {
      const screenWidth = getScreen().width;
      this.setState({
        dynamicGraphSize: isSmallViewport(),
        screenWidth,
      });
    };

    setDynamicSizes();

    this.resizeListener = () => {
      setDynamicSizes();
    };

    window.addEventListener('optimizedResize', this.resizeListener);
  }

  render() {
    if (this.state.isLoaded) {
      return (
        h(MuiThemeProvider, {
          theme: theme,
        }, h(OptedInContext.Provider, {
          value: {
            optedIn: this.state.isOptedIn,
            setOptedIn: this.setOptedIn,
          },
        }, [
          h(Header, {
            key: 'header',
            csrf: this.state.csrf,
            isLoggedIn: this.state.isLoggedIn,
            isAdmin: this.state.isAdmin,
            userStatus: this.state.userStatus,
            googleUser: this.state.googleUser,
            logoutClick: this.logout,
            loginClick: this.login,
            maintenanceMode: this.state.maintenanceMode,
            needsDistrictConfig: this.state.needsDistrictConfig,
            handleSettingsOpen: this.handleSettingsOpen,
            mobileLogin: this.state.mobileLogin,
          }, null),
          this.state.errorPage ?
          h(ErrorPage, {
            errorType: this.state.errorPage,
          }) :
          h(Main, {
            key: 'main',
            csrf: this.state.csrf,
            isLoggedIn: this.state.isLoggedIn,
            isAdmin: this.state.isAdmin,
            history: this.props.history,
            userStatus: this.state.userStatus,
            googleUser: this.state.googleUser,
            logoutClick: this.logout,
            loginClick: this.login,
            errorMessage: this.state.errorMessage,
            fetchUserStatus: this.fetchUserStatus,
            dynamicGraphSize: this.state.dynamicGraphSize,
            screenWidth: this.state.screenWidth,
            setReportName: this.setCurrentReportName,
            setLatestReportName: this.setLatestReportName,
            productWindowsLoading: this.state.productWindowsLoading,
            setProductWindowsLoading: this.setProductWindowsLoading,
            scopesGranted: this.state.scopesGranted,
            promptScopes: () => this.promptScopes(),
          }, null),
          h(Footer, {
            key: 'footer',
          }, null),

          h(Dialog, {
            key: 'settings-modal',
            open: this.state.isSettingsOpen,
            onClose: this.state.settingsModalId === 'primary' &&
                this.state.allowModalClose ? this.handleSettingsClose : null,
            maxWidth: false,
          }, h(Settings, {
            modalId: this.state.settingsModalId,
            csrf: this.state.csrf,
            googleUser: this.state.googleUser,
            userStatus: this.state.userStatus,
            currentReportName: this.state.currentReportName,
            handleSettingsClose: this.handleSettingsClose,
            allowModalClose: this.state.allowModalClose,
            setAllowModalClose: this.setAllowModalClose,
            settingsModalId: this.state.settingsModalId,
            setSettingsModalId: this.setSettingsModalId,
            setProductWindowsLoading: this.setProductWindowsLoading,
            isAdmin: this.state.isAdmin,
            saveRequired: this.state.settingsSaveRequired,
            latestReportName: this.state.latestReportName,
            onLatestReport:
              this.state.currentReportName === this.state.latestReportName,
          }, null)),
          !this.state.errorPage ? h(GdprAnnouncement, null, null) : null,
        ])
        ));
    } else {
      window.addEventListener('load', this.onLoad);
      return (
        h(LoadingIndicator, null, null)
      );
    }
  }
}

export default withRouter(App);
